import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

declare const echarts: any;

export interface EChartDataZoomRange {
  start: number;
  end: number;
  startValue?: string | number;
  endValue?: string | number;
}

export interface EChartValueRange {
  min: number;
  max: number;
}

export type EChartLegendSelection = Record<string, boolean>;
export type EChartAxisPointerValue = string | number | undefined;

@Component({
  selector: 'app-echart',
  template: `<div #chartEl [style.height]="height" class="w-full"></div>`,
})
export class EChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() public options: any;
  @Input() public height: string = '300px';
  @Output() public dataZoomChange = new EventEmitter<EChartDataZoomRange>();
  @Output() public legendSelectionChange = new EventEmitter<EChartLegendSelection>();
  @Output() public axisPointerChange = new EventEmitter<EChartAxisPointerValue>();

  @ViewChild('chartEl', { static: true }) private readonly chartEl!: ElementRef<HTMLDivElement>;

  private chart: any;
  private resizeObserver?: ResizeObserver;
  private readonly onDataZoom = (): void => {
    const range = this.getDataZoomRange();
    if (range) {
      this.dataZoomChange.emit(range);
    }
  };
  private readonly onLegendSelectionChange = (event: { selected?: EChartLegendSelection }): void => {
    if (event.selected) {
      this.legendSelectionChange.emit({ ...event.selected });
    }
  };
  private readonly onAxisPointerChange = (event: {
    axesInfo?: Array<{ axisDim?: string; axisIndex?: number; value?: unknown }>;
  }): void => {
    const axisInfo = event.axesInfo?.find(axis => axis.axisDim === 'x' && axis.axisIndex === 0);
    const axisData = this.chart?.getOption()?.xAxis?.[0]?.data ?? [];
    this.axisPointerChange.emit(this.getCategoryValue(axisInfo?.value, axisData));
  };

  public ngAfterViewInit(): void {
    this.chart = echarts.init(this.chartEl.nativeElement, null, { renderer: 'canvas' });

    this.resizeObserver = new ResizeObserver(() => {
      this.chart?.resize();
    });
    this.resizeObserver.observe(this.chartEl.nativeElement);
    this.chart.on('datazoom', this.onDataZoom);
    this.chart.on('legendselectchanged', this.onLegendSelectionChange);
    this.chart.on('updateAxisPointer', this.onAxisPointerChange);

    if (this.options) {
      this.chart.setOption(this.options, true);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && this.chart && this.options) {
      this.chart.setOption(this.options, true);
    }
  }

  public restore(): void {
    this.chart?.dispatchAction({ type: 'restore' });
  }

  public getDataZoomRange(): EChartDataZoomRange | undefined {
    const option = this.chart?.getOption();
    const range = option?.dataZoom?.[0];
    if (typeof range?.start !== 'number' || typeof range.end !== 'number') {
      return undefined;
    }

    const axisData = option?.xAxis?.[0]?.data ?? [];
    const startValue = this.getCategoryValue(range.startValue, axisData);
    const endValue = this.getCategoryValue(range.endValue, axisData);

    return {
      start: range.start,
      end: range.end,
      startValue,
      endValue,
    };
  }

  public setDataZoomRange(range: EChartDataZoomRange): void {
    const currentRange = this.getDataZoomRange();
    const { startValue, endValue } = range;
    const hasValueRange = startValue !== undefined && endValue !== undefined;
    const isSameRange = hasValueRange
      ? currentRange?.startValue === startValue && currentRange?.endValue === endValue
      : currentRange?.start === range.start && currentRange?.end === range.end;

    if (isSameRange) {
      return;
    }

    const action: Record<string, string | number> = {
      type: 'dataZoom',
    };

    if (hasValueRange) {
      action['startValue'] = startValue;
      action['endValue'] = endValue;
    } else {
      action['start'] = range.start;
      action['end'] = range.end;
    }

    this.chart?.dispatchAction(action, { silent: true });
  }

  public getVisibleValueRange(range: EChartDataZoomRange | undefined = this.getDataZoomRange()): EChartValueRange | undefined {
    const option = this.chart?.getOption();
    const axisData: Array<string | number> = option?.xAxis?.[0]?.data ?? [];
    if (!axisData.length) {
      return undefined;
    }

    const startIndex = this.getCategoryIndex(range?.startValue, range?.start, axisData, false);
    const endIndex = this.getCategoryIndex(range?.endValue, range?.end, axisData, true);
    const selected: EChartLegendSelection = option?.legend?.[0]?.selected ?? {};
    const values = (option?.series ?? [])
      .filter((series: { name?: string }) => !series.name || selected[series.name] !== false)
      .flatMap((series: { data?: unknown[] }) => (series.data ?? []).slice(startIndex, endIndex + 1))
      .filter((value: unknown) => value !== null && value !== undefined)
      .map(Number)
      .filter((value: number) => Number.isFinite(value));

    return values.length
      ? { min: Math.min(...values), max: Math.max(...values) }
      : undefined;
  }

  public setYAxisRange(range?: EChartValueRange): void {
    this.chart?.setOption({
      yAxis: [{
        min: range?.min ?? null,
        max: range?.max ?? null,
      }],
    });
  }

  public setAxisPointer(value: EChartAxisPointerValue): void {
    if (!this.chart) {
      return;
    }

    if (value === undefined) {
      this.chart.dispatchAction({
        type: 'updateAxisPointer',
        currTrigger: 'leave',
      }, { silent: true });
      return;
    }

    const x = this.chart.convertToPixel({ xAxisIndex: 0 }, value);
    if (typeof x !== 'number' || !Number.isFinite(x)) {
      return;
    }

    this.chart.dispatchAction({
      type: 'updateAxisPointer',
      currTrigger: 'mousemove',
      x,
      y: this.chart.getHeight() / 2,
    }, { silent: true });
  }

  public getLegendSelection(): EChartLegendSelection {
    const option = this.chart?.getOption();
    const selected = option?.legend?.[0]?.selected ?? {};
    const seriesNames = (option?.series ?? [])
      .map((series: { name?: unknown }) => series.name)
      .filter((name: unknown): name is string => typeof name === 'string');

    return Object.fromEntries(
      seriesNames.map((name: string) => [name, selected[name] !== false])
    );
  }

  public setLegendSelection(selection: EChartLegendSelection): void {
    const currentSelection = this.getLegendSelection();

    for (const [name, selected] of Object.entries(selection)) {
      if (!(name in currentSelection) || currentSelection[name] === selected) {
        continue;
      }

      this.chart?.dispatchAction({
        type: selected ? 'legendSelect' : 'legendUnSelect',
        name,
      }, { silent: true });
    }
  }

  private getCategoryValue(
    value: unknown,
    axisData: Array<string | number>
  ): string | number | undefined {
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value < axisData.length) {
      return axisData[value];
    }

    return typeof value === 'string' || typeof value === 'number' ? value : undefined;
  }

  private getCategoryIndex(
    value: string | number | undefined,
    percentage: number | undefined,
    axisData: Array<string | number>,
    roundUp: boolean
  ): number {
    if (value !== undefined) {
      const valueIndex = axisData.indexOf(value);
      if (valueIndex >= 0) {
        return valueIndex;
      }
    }

    const boundedPercentage = Math.min(100, Math.max(0, percentage ?? (roundUp ? 100 : 0)));
    const index = boundedPercentage / 100 * (axisData.length - 1);
    return roundUp ? Math.ceil(index) : Math.floor(index);
  }

  public saveAsImage(filename: string = 'chart'): void {
    if (!this.chart) return;
    const url: string = this.chart.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' });
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${filename}.png`;
    anchor.click();
  }

  public enterFullscreen(): void {
    const el = this.chartEl.nativeElement.parentElement ?? this.chartEl.nativeElement;
    el.requestFullscreen?.();
  }

  public ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.chart?.off('datazoom', this.onDataZoom);
    this.chart?.off('legendselectchanged', this.onLegendSelectionChange);
    this.chart?.off('updateAxisPointer', this.onAxisPointerChange);
    this.chart?.dispose();
  }
}
